import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  Song,
  SongsData,
  SongsQueryParams,
  CreateTrackRequest,
  UpdateTrackRequest,
} from '../../types/api/song';

export class SongService {
  private httpClient = HttpClient.getInstance();

  async getSongs(params: SongsQueryParams = {}): Promise<SongsData> {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.append('page', String(params.page));
    if (params.size !== undefined) query.append('size', String(params.size));
    if (params.keyword) query.append('keyword', params.keyword);
    if (params.albumId) query.append('albumId', params.albumId);
    const url = `${API_ENDPOINTS.SONGS.ADMIN_LIST}?${query}`;
    const response = await this.httpClient.get<SongsData>(url);
    if (!response.success) throw new Error(response.message || 'Failed to fetch songs');
    return response.data;
  }

  async getSong(trackId: string): Promise<Song> {
    const response = await this.httpClient.get<Song>(API_ENDPOINTS.SONGS.ADMIN_DETAIL(trackId));
    if (!response.success) throw new Error(response.message || 'Failed to fetch song');
    return response.data;
  }

  /**
   * Create a track. Sends multipart/form-data: `data` (JSON) + optional `audio` (MP3) file.
   */
  async createTrack(trackData: CreateTrackRequest, audioFile?: File): Promise<Song> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(trackData)], { type: 'application/json' });
    formData.append('data', jsonBlob, 'data.json');
    if (audioFile) {
      formData.append('audio', audioFile, audioFile.name);
    }

    const response = await this.httpClient.request<Song>({
      method: 'POST',
      url: API_ENDPOINTS.SONGS.ADMIN_CREATE,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (!response.success) throw new Error(response.message || 'Failed to create track');
    return response.data;
  }

  /**
   * Update a track. Sends multipart/form-data: `data` (JSON) + optional replacement `audio` file.
   */
  async updateTrack(trackId: string, trackData: UpdateTrackRequest, audioFile?: File): Promise<Song> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(trackData)], { type: 'application/json' });
    formData.append('data', jsonBlob, 'data.json');
    if (audioFile) {
      formData.append('audio', audioFile, audioFile.name);
    }

    const response = await this.httpClient.request<Song>({
      method: 'PUT',
      url: API_ENDPOINTS.SONGS.ADMIN_UPDATE(trackId),
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (!response.success) throw new Error(response.message || 'Failed to update track');
    return response.data;
  }

  async deleteTrack(trackId: string): Promise<void> {
    const response = await this.httpClient.delete<{ message: string }>(
      API_ENDPOINTS.SONGS.ADMIN_DELETE(trackId)
    );
    if (!response.success) throw new Error(response.message || 'Failed to delete track');
  }
}
